"use client";

import { useAppSelector } from "../../_hooks/hooks";
import dynamic from "next/dynamic";
import Link from "next/link";

const Preloader = dynamic(() =>
  import("../../components/Preloader").then((mod) => mod.Preloader),
);
const QuickInfoSection = dynamic(() => import("./components/QuickInfoSection"));
const WhyVisitSection = dynamic(() => import("./components/WhyVisitSection"));
const GallerySection = dynamic(() => import("./components/GallerySection"));
const BestTimeToVisitSection = dynamic(
  () => import("./components/BestTimeToVisitSection"),
);
const HowToReachSection = dynamic(
  () => import("./components/HowToReachSection"),
);
const TipsSection = dynamic(() => import("./components/TipsSection"));
const NearbyAttractionsSection = dynamic(
  () => import("./components/NearbyAttractionsSection"),
);

const Button = dynamic(() =>
  import("../../components/ui/button").then((mod) => mod.Button),
);

export function AttractionDetail({ initialData }: { initialData?: any }) {
  const loading = useAppSelector((state: any) => state.attraction.loading);
  const AttractionDetail = initialData;

  return (
    <>
      {loading && <Preloader />}
      <div className="min-h-screen bg-white text-md">
        {/* Hero Section */}
        <section className="relative min-h-[70vh] flex items-end overflow-hidden">
          <div
            className="absolute inset-0 z-0"
            style={{
              backgroundImage: `url('${AttractionDetail?.BannerImage}')`,
              backgroundSize: "cover",
              backgroundPosition: "center",
            }}
          >
            <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-black/30 to-black/70" />
          </div>

          <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10 mt-25 mb-15">
            <div className="max-w-4xl">
              <div className="inline-block px-4 py-2 bg-white/10 backdrop-blur-sm border border-white/20 rounded-full mb-6">
                <p className="text-[#C9A962] tracking-[0.3em] text-sm">
                  {AttractionDetail?.AttractionCategory || "ATTRACTION"}
                </p>
              </div>

              <h1 className="text-[#C9A962] text-3xl md:text-4xl lg:text-5xl mb-6 leading-tight font-playfair">
                {AttractionDetail?.Title}
              </h1>

              <p className="text-white/95 text-2xl font-playfair">
                {AttractionDetail?.TagLine}
              </p>
            </div>
          </div>
        </section>

        <QuickInfoSection
          duration={AttractionDetail?.Duration}
          distance={AttractionDetail?.Distance}
          bestTime={AttractionDetail?.BestTime}
          travelTime={AttractionDetail?.TravelTime}
        />

        {/* Description */}
        {AttractionDetail?.Description && (
          <section className="py-24 bg-white">
            <div className="container mx-auto px-4 sm:px-6 lg:px-8">
              <div className="max-w-4xl mx-auto">
                <h2 className="text-2xl md:text-4xl mb-8 text-[#1A1A1A]">
                  About This Attraction
                </h2>
                <div className="w-24 h-1 bg-[#C9A962] mb-8" />
                <p
                  className="text-gray-700 leading-relaxed mb-6 bulletin-section text-justify space-y-6"
                  dangerouslySetInnerHTML={{
                    __html: AttractionDetail?.Description,
                  }}
                />
              </div>
            </div>
          </section>
        )}

        <WhyVisitSection whyVisit={AttractionDetail?.WhyVisit} />

        <GallerySection
          images={AttractionDetail?.Images}
          title={AttractionDetail?.Title || "Attraction"}
        />

        <BestTimeToVisitSection bestTimes={AttractionDetail?.BestTimes} />

        <HowToReachSection howToReach={AttractionDetail?.HowToReach} />

        <TipsSection
          dos={AttractionDetail?.Dos}
          donts={AttractionDetail?.Donts}
        />

        <NearbyAttractionsSection nearby={AttractionDetail?.Nearby} />

        {/* CTA */}
        {AttractionDetail?.PropertyID && (
          <section className="py-24 bg-[#1A1A1A]">
            <div className="container mx-auto px-4 sm:px-6 lg:px-8 text-center">
              <h2 className="text-white text-4xl md:text-5xl mb-8">
                Plan Your Visit
              </h2>
              <p className="text-white/90 text-xl max-w-2xl mx-auto mb-12">
                Stay at Kavish and explore the best of{" "}
                {AttractionDetail?.PropertyName || "our resort"}
              </p>
              <Link href={`/${AttractionDetail?.PropertyURL || ""}/`}>
                <Button
                  size="lg"
                  className="bg-[#B42025] hover:bg-[#8F1A1E] text-white px-10"
                >
                  View Our Resorts
                </Button>
              </Link>
            </div>
          </section>
        )}
      </div>
    </>
  );
}
